import React, { useMemo, useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Link, useHistory } from "react-router-dom";

import PageLayout from "../_layout/PageLayout";
import { SectionMenuItemsModel } from "../sewer-objects/section-details/SectionMenuItems";
import RunVideoAnalysisButton from "../../components/buttons/run-video-analysis-button/RunVideoAnalysisButton";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import { CurrentSectionStorageModel } from "../../services/sewer-object/CurrentSectionStorage";
import { translationService } from "../../services/translation/TranslationService";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import {
  CreateDamage,
  ViewDamagesFromAssignedProject,
  ViewDamagesList,
  RunVideoAnalysis,
  CreateDamageInAssignedProject,
  RunVideoAnalysisFromAssignedProject,
} from "../../services/authorization/Permissions";
import { VideoAnalysisServiceModel } from "../../services/inspection/VideoAnalysisService";
import { api } from "../../services/api/ApiService";
import useErrorHandler, {
  ApplicationError,
  errorHandler,
} from "../../services/error-handler/ErrorHandlerService";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { Notification } from "../../services/notifications/NotificationsService";
import { PubSubEvents } from "../../services/PubSubEvents";
import { SubscriptionPlanUsageStorageModel } from "../../services/organization-subscription-plans/SubscriptionPlanUsageStorage";
import { CurrentInspectionStorageModel } from "../../services/inspection/CurrentInspectionStorage";
import useUrlParams from "../../services/url/UrlParamsService";
import { globalModule } from "../../services/service-provider/GlobalModule";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { AddNewDamageButton } from "../damages/components/buttons/AddNewDamageButton";
import DamagesVideo from "./components/DamagesVideo";
import DamagesVideoAnalysis from "./components/DamagesVideoAnalysis";
import { DamagesStorageModel } from "../../services/damage/DamagesStorage";
import { featureFlagsStorage } from "../../services/feature-flags/FeatureFlagsStorage";
import VideoAnalysisDiagram from "./components/video-analysis-diagram/VideoAnalysisDiagram";
import RunHighlightDetectionButton from "../../components/buttons/run-highlight-detection-button/RunHighlightDetectionButton";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import useRouteService from "../../services/route-service/RouteService";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";
import Svg from "../../components/svg/Svg";
import { Info } from "../../components/svg/svgImageList";
import DamagesTable from "./components/DamagesTable";
import Tools from "./components/video-analysis-diagram/tools/Tools";
import VideoTools from "./components/video-analysis-diagram/tools/VideoTools";
import { Flags } from "../../generated/FeatureFlags";
import { AnalysisConfidenceStorageModel } from "../../services/organization-analysis-confidence/AnalysisConfidenceStorage";

const AnalysisPage = observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const currentSectionId = urlParamsService.getSectionId()!;
  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create(
      {
        currentSectionId: currentSectionId,
        parentEntityFromPath: {
          id: urlParamsService.getSewerObjectParentId()!,
          type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job,
        },
      },
      { api }
    )
  );

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const [analysisConfidenceStorage] = useState(() =>
    AnalysisConfidenceStorageModel.create({ autoLoad: false }, { api })
  );

  const { jumpTo } = videoPlayerService;

  useEffect(() => {
    globalModule.videoService = {
      ...globalModule.videoService,
      jumpTo,
    };
  }, [jumpTo]);

  const [planUsageStorage] = useState(() =>
    SubscriptionPlanUsageStorageModel.create({ autoLoad: false }, { api })
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
      section: currentSectionStorage.currentSection,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [
    currentSectionStorage.currentSectionId,
    currentSectionStorage.parentEntityFromPath,
    currentSectionStorage.currentSection,
  ]);

  const videoAnalysis = useMemo(() => {
    if (currentInspectionStorage.currentInspection.hasVideo)
      return VideoAnalysisServiceModel.create(
        {
          videoId: currentInspectionStorage.currentInspection.video!.id,
          language: translationService.language,
        },
        { api: api, errorHandler: errorHandler }
      );
  }, [
    currentInspectionStorage.currentInspection.hasVideo,
    currentInspectionStorage.currentInspection.video,
  ]);

  const damagesStorage = useMemo(() => {
    if (currentInspectionStorage.currentInspection.hasVideo)
      return DamagesStorageModel.create(
        { inspectionId: currentInspectionId, noPagination: true, preview: true },
        { api }
      );
  }, [currentInspectionId, currentInspectionStorage.currentInspection.hasVideo]);

  useEffect(() => {
    var conversionSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoConversionFinished,
      async (event: string, notification: Notification) => {
        if (currentInspectionStorage.currentInspection.video?.id === notification.data.fileId) {
          await currentInspectionStorage.reloadCurrentInspection();
        }
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(conversionSubscribeToken);
    };
  }, [currentInspectionStorage, currentInspectionStorage.reloadCurrentInspection]);

  useEffect(() => {
    if (currentInspectionStorage.currentInspection.hasVideo)
      planUsageStorage.getOrganizationVideoAnalysisCallsUsage();
  }, [currentInspectionStorage.currentInspection.hasVideo, planUsageStorage]);

  useEffect(() => {
    const getDamages = async () => {
      try {
        await damagesStorage?.getDamagesForPlayer(currentInspectionStorage.currentInspectionId);
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withErrorMessage("default-error.damage.video-player-timestamps");
        handleErrorInSnackbar(applicationError);
      }
    };

    if (damagesStorage) getDamages();
  }, [currentInspectionStorage.currentInspectionId, damagesStorage, handleErrorInSnackbar]);

  const isPageLoading = useMemo(
    () =>
      currentSectionStorage.isLoading ||
      currentInspectionStorage.isLoading ||
      (currentInspectionStorage.currentInspection.hasVideo &&
        planUsageStorage.availableCallsLoading) ||
      (currentInspectionStorage.currentInspection.hasVideo && damagesStorage?.isLoading),
    [
      currentSectionStorage.isLoading,
      currentInspectionStorage.isLoading,
      currentInspectionStorage.currentInspection.hasVideo,
      planUsageStorage.availableCallsLoading,
      damagesStorage?.isLoading,
    ]
  );

  const getDamagesAreLockedWarning = useCallback(() => {
    if (
      currentInspectionStorage.inspectionIsLockedBySomeoneElse &&
      currentInspectionStorage.userWithLockOnInspection
    )
      return {
        key: "DamagesPage.locked-inspection",
        context: { userName: currentInspectionStorage.userWithLockOnInspection.name },
      };
    else return undefined;
  }, [
    currentInspectionStorage.inspectionIsLockedBySomeoneElse,
    currentInspectionStorage.userWithLockOnInspection,
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const isCreateDamageDisabled = useMemo(
    () =>
      currentInspectionStorage.inspectionIsLockedBySomeoneElse ||
      !permissionsService.userHasOneOfThePermissions([
        CreateDamage,
        CreateDamageInAssignedProject,
      ]) ||
      (currentInspectionStorage.currentInspection.video?.conversionNotAvailable ?? true),
    [
      currentInspectionStorage.currentInspection.video?.conversionNotAvailable,
      currentInspectionStorage.inspectionIsLockedBySomeoneElse,
    ]
  );

  const isRunVideoAnalysisDisabled = useMemo(() => {
    return (
      !currentInspectionStorage.currentInspection.hasVideo ||
      (currentInspectionStorage.currentInspection.hasVideo && videoAnalysis?.analysisInProgress) ||
      !permissionsService.userHasOneOfThePermissions([
        RunVideoAnalysis,
        RunVideoAnalysisFromAssignedProject,
      ]) ||
      videoAnalysis?.isLoading ||
      videoAnalysis?.segmentationInProgress ||
      planUsageStorage.hasReachedVideoAnalysisLimitForFreeSubscription ||
      currentInspectionStorage.currentInspection.video?.conversionFailed
    );
  }, [
    currentInspectionStorage.currentInspection.hasVideo,
    currentInspectionStorage.currentInspection.video?.conversionFailed,
    planUsageStorage.hasReachedVideoAnalysisLimitForFreeSubscription,
    videoAnalysis?.analysisInProgress,
    videoAnalysis?.isLoading,
    videoAnalysis?.segmentationInProgress,
  ]);

  const isRunHighlightDetectionDisabled = useMemo(() => {
    return (
      videoAnalysis?.segmentationInProgress ||
      !permissionsService.userHasOneOfThePermissions([
        RunVideoAnalysis,
        RunVideoAnalysisFromAssignedProject,
      ]) ||
      videoAnalysis?.isLoading ||
      videoAnalysis?.analysisFailed ||
      planUsageStorage.hasReachedVideoAnalysisLimitForFreeSubscription ||
      currentInspectionStorage.currentInspection.video?.conversionFailed
    );
  }, [
    currentInspectionStorage.currentInspection.video?.conversionFailed,
    planUsageStorage.hasReachedVideoAnalysisLimitForFreeSubscription,
    videoAnalysis?.analysisFailed,
    videoAnalysis?.isLoading,
    videoAnalysis?.segmentationInProgress,
  ]);

  const handleVideoAnalysisClick = useCallback(async () => {
    try {
      await videoAnalysis?.startVideoAnalysis();

      if (
        currentInspectionStorage.currentInspection.hasVideo &&
        currentInspectionStorage.currentInspection.video?.isConverting
      ) {
        snackbarNotificationsService.showSuccess(
          "damages.DamagesVideoAnalysis.video-file-waiting-conversion"
        );
      } else if (
        currentInspectionStorage.currentInspection.hasVideo &&
        currentInspectionStorage.currentInspection.video?.isAvailable
      ) {
        snackbarNotificationsService.showSuccess(
          "damages.DamagesVideoAnalysis.video-analysis-started-snackbar"
        );
      }
    } catch (error) {}
  }, [
    currentInspectionStorage.currentInspection.video,
    currentInspectionStorage.currentInspection.hasVideo,
    videoAnalysis,
  ]);

  const handleRunHighlightDetectionClick = useCallback(async () => {
    try {
      let bothHDandVAStarted = false;
      if (videoAnalysis?.analysisNotYetStarted) {
        await videoAnalysis?.startVideoAnalysis();
        bothHDandVAStarted = true;
      }
      await videoAnalysis?.startSegmentation();

      if (
        currentInspectionStorage.currentInspection.hasVideo &&
        currentInspectionStorage.currentInspection.video?.isConverting
      ) {
        snackbarNotificationsService.showSuccess(
          "damages.DamagesVideoAnalysis.video-file-waiting-conversion"
        );
      } else if (bothHDandVAStarted) {
        snackbarNotificationsService.showSuccess("VideoAnalysis.va-highlight-detection-started");
      } else if (
        currentInspectionStorage.currentInspection.hasVideo &&
        currentInspectionStorage.currentInspection.video?.isAvailable &&
        videoAnalysis?.isAnalyzed
      ) {
        snackbarNotificationsService.showSuccess("VideoAnalysis.highlight-detection-started");
      }
    } catch (error) {}
  }, [
    currentInspectionStorage.currentInspection.video,
    currentInspectionStorage.currentInspection.hasVideo,
    videoAnalysis,
  ]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([
        {
          entity: currentSectionStorage.parentEntityFromPath.type,
          name: await currentSectionStorage.getParentEntityName(
            currentSectionStorage.currentSection.project.name
          ),
        },
        {
          entity: EntityTypeFromPath.SewerObject,
          name: currentSectionStorage.currentSection.name,
        },
        {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName(),
        },
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname,
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewDamagesList, ViewDamagesFromAssignedProject]}
      titleKey="AnalysisPage.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
      }}
      menuItems={menuItems}
      isLoading={isPageLoading}
    >
      {!currentInspectionStorage.currentInspection.hasVideo ? (
        <div className="message-info">
          <Svg className="svg-info" svg={Info} />
          <span>
            {translationService.translate("VideoAnalysis.no-video-uploaded-part1")}
            <Link to={`files`}>
              {translationService.translate("VideoAnalysis.no-video-uploaded-part2")}
            </Link>
            {translationService.translate("VideoAnalysis.no-video-uploaded-part3")}
          </span>
        </div>
      ) : (
        <>
          <ActionsSection
            warningKey={getDamagesAreLockedWarning()?.key}
            warningContext={getDamagesAreLockedWarning()?.context}
          >
            <ButtonsSection>
              <AddNewDamageButton
                disabled={isCreateDamageDisabled}
                videoAnalysisService={videoAnalysis}
                currentInspectionStorage={currentInspectionStorage}
              />
              <RunVideoAnalysisButton
                disabled={isRunVideoAnalysisDisabled}
                confirmationAction={handleVideoAnalysisClick}
              />
              {featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) && (
                <RunHighlightDetectionButton
                  disabled={isRunHighlightDetectionDisabled}
                  confirmationAction={handleRunHighlightDetectionClick}
                  shouldDisplayModalForTwoCallsPerformed={videoAnalysis?.analysisNotYetStarted!}
                />
              )}
            </ButtonsSection>
          </ActionsSection>
          <div className="analysis-container">
            <DamagesTable damagesStorage={damagesStorage!} />
            <div className="video-container">
              <DamagesVideo
                currentInspectionStorage={currentInspectionStorage}
                damagesStorage={damagesStorage!}
                videoAnalysis={videoAnalysis}
              />
              {videoAnalysis &&
                (featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform) ? (
                  <VideoAnalysisDiagram
                    videoAnalysis={videoAnalysis}
                    currentInspectionStorage={currentInspectionStorage}
                    subscriptionPlanUsageStorage={planUsageStorage}
                    damagesStorage={damagesStorage!}
                  />
                ) : (
                  <DamagesVideoAnalysis
                    videoAnalysis={videoAnalysis}
                    currentInspectionStorage={currentInspectionStorage}
                    subscriptionPlanUsageStorage={planUsageStorage}
                    damagesStorage={damagesStorage!}
                  />
                ))}
            </div>
            <div className="legends-container">
              {videoAnalysis &&
                videoAnalysis.segmentationWasSuccessful &&
                featureFlagsStorage.isFeatureEnabled(Flags.Show_Video_Segmentations) &&
                featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform) && (
                  <VideoTools
                    videoAnalysisService={videoAnalysis}
                    analysisConfidenceStorage={analysisConfidenceStorage}
                  />
                )}
              {videoAnalysis &&
                featureFlagsStorage.isFeatureEnabled(Flags.Render_VA_Diagram_In_Platform) && (
                  <Tools
                    videoAnalysisService={videoAnalysis}
                    analysisConfidenceStorage={analysisConfidenceStorage}
                  />
                )}
            </div>
          </div>
        </>
      )}
    </PageLayout>
  );
});

export default AnalysisPage;
